import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/src/components/Box";
import Button from "@memorang/ui/src/components/Button";
import type { IconType } from "@memorang/ui/src/types";
import { useTranslation } from "react-i18next";

/* -----------------Props--------------- */
export type NextButtonProps = {
	handlePrevious: () => void;
	handleNext: () => void;
	showButtonPrevious?: boolean;
	showButtonNavSkip?: boolean;
	showButtonNavNext?: boolean;
	showButtonNavFinish?: boolean;
	disableNextButton?: boolean;
	disablePreviousButton?: boolean;
	nextButtonText?: string;
	prevButtonText?: string;
	progressText?: string;
	isSummativeTest?: boolean;
	toggleHelpDialog?: () => void;
	inReviewMode?: boolean;
};

const PreviousButton = ({
	showButtonPrevious,
	handlePrevious,
	disablePreviousButton,
	prevButtonText,
	isSummativeTest,
}: {
	showButtonPrevious?: boolean;
	handlePrevious: () => void;
	disablePreviousButton?: boolean;
	prevButtonText?: string;
	isSummativeTest?: boolean;
}) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	return (
		<>
			{showButtonPrevious ? (
				<Button
					mode="outlined"
					textColor={isSummativeTest ? theme.colors.white : undefined}
					icon={"chevron-left"}
					accessible
					accessibilityLabel="Previous question"
					contentStyle={{
						borderColor: isSummativeTest
							? theme.colors.white
							: theme.colors.divider,
					}}
					onPress={handlePrevious}
					disabled={disablePreviousButton}
				>
					{prevButtonText || t("session.navigation.previous")}
				</Button>
			) : (
				<Box width={96} />
			)}
		</>
	);
};

const NextButton = ({
	nextButtonText,
	handleNext,
	disableNextButton,
	isSummativeTest,
	showButtonNavSkip,
	nextButtonIcon,
	nextButtonMode,
	bg,
}: {
	nextButtonText?: string;
	handleNext: () => void;
	disableNextButton?: boolean;
	isSummativeTest?: boolean;
	showButtonNavSkip?: boolean;
	nextButtonIcon: IconType;
	nextButtonMode: "outlined" | "elevated" | "contained";
	bg: string;
}) => {
	const theme = useAppTheme();
	return (
		<Button
			onPress={handleNext}
			mode={nextButtonMode}
			textColor={
				isSummativeTest
					? showButtonNavSkip
						? theme.colors.white
						: theme.colors.primary
					: undefined
			}
			asset="endIcon"
			icon={nextButtonIcon}
			contentStyle={{
				flexDirection: "row-reverse",
				borderColor: isSummativeTest
					? theme.colors.white
					: theme.colors.divider,
				backgroundColor: disableNextButton ? theme.colors.disabled : bg,
			}}
			disabled={disableNextButton}
			accessibilityLabel={`${nextButtonText} question`}
			accessible
		>
			{nextButtonText}
		</Button>
	);
};

type TestButtonsProps = Pick<
	NextButtonProps,
	| "showButtonPrevious"
	| "handlePrevious"
	| "disablePreviousButton"
	| "prevButtonText"
	| "showButtonNavSkip"
	| "nextButtonText"
	| "handleNext"
	| "disableNextButton"
	| "isSummativeTest"
> & {
	nextButtonIcon: IconType;
	bg: string;
	nextButtonMode: "outlined" | "elevated" | "contained";
};

const TestNavButtons = ({
	showButtonPrevious,
	handlePrevious,
	disablePreviousButton,
	prevButtonText,
	showButtonNavSkip,
	nextButtonText,
	handleNext,
	disableNextButton,
	isSummativeTest,
	nextButtonIcon,
	nextButtonMode,
	bg,
}: TestButtonsProps) => {
	const { isMobile } = useDevice();

	if (!isSummativeTest) {
		return (
			<>
				<PreviousButton
					showButtonPrevious={showButtonPrevious}
					handlePrevious={handlePrevious}
					disablePreviousButton={disablePreviousButton}
					prevButtonText={prevButtonText}
					isSummativeTest={isSummativeTest}
				/>
				<NextButton
					nextButtonText={nextButtonText}
					handleNext={handleNext}
					disableNextButton={disableNextButton}
					isSummativeTest={isSummativeTest}
					showButtonNavSkip={showButtonNavSkip}
					nextButtonIcon={nextButtonIcon}
					nextButtonMode={nextButtonMode}
					bg={bg}
				/>
			</>
		);
	}

	if (isMobile) {
		return (
			<>
				<PreviousButton
					showButtonPrevious={showButtonPrevious}
					handlePrevious={handlePrevious}
					disablePreviousButton={disablePreviousButton}
					prevButtonText={prevButtonText}
					isSummativeTest={isSummativeTest}
				/>
				<NextButton
					nextButtonText={nextButtonText}
					handleNext={handleNext}
					disableNextButton={disableNextButton}
					isSummativeTest={isSummativeTest}
					showButtonNavSkip={showButtonNavSkip}
					nextButtonIcon={nextButtonIcon}
					nextButtonMode={nextButtonMode}
					bg={bg}
				/>
			</>
		);
	}
	return (
		<Box flexDirection="row" gap={16}>
			<PreviousButton
				showButtonPrevious={showButtonPrevious}
				handlePrevious={handlePrevious}
				disablePreviousButton={disablePreviousButton}
				prevButtonText={prevButtonText}
				isSummativeTest={isSummativeTest}
			/>
			<NextButton
				nextButtonText={nextButtonText}
				handleNext={handleNext}
				disableNextButton={disableNextButton}
				isSummativeTest={isSummativeTest}
				showButtonNavSkip={showButtonNavSkip}
				nextButtonIcon={nextButtonIcon}
				nextButtonMode={nextButtonMode}
				bg={bg}
			/>
		</Box>
	);
};

/* -----------------Component--------------- */
const NavButtons = ({
	handlePrevious,
	handleNext,
	showButtonPrevious,
	showButtonNavSkip,
	showButtonNavNext,
	showButtonNavFinish,
	disableNextButton,
	disablePreviousButton,
	nextButtonText,
	prevButtonText,
	isSummativeTest,
	inReviewMode,
	toggleHelpDialog,
}: NextButtonProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const nextButtonMode =
		showButtonNavSkip || disableNextButton
			? "outlined"
			: isSummativeTest
				? "elevated"
				: "contained";

	const getNextButtonText = () => {
		switch (true) {
			case showButtonNavSkip:
				return t("session.navigation.skip");
			case showButtonNavFinish:
				return t("session.navigation.finish");
			case showButtonNavNext:
				return nextButtonText || t("session.navigation.next");
			default:
				return t("session.navigation.finish");
		}
	};

	const nextButtonIcon = showButtonNavSkip
		? "skip-next-outline"
		: "chevron-right";

	const getNextButtonBackgroundColor = () => {
		if (isSummativeTest) {
			if (showButtonNavSkip) {
				return "transparent";
			}
			return theme.colors.onPrimary;
		}
		return showButtonNavSkip ? "transparent" : theme.colors.primary;
	};

	const bg = getNextButtonBackgroundColor();

	return (
		<Box
			flexDirection="row"
			justifyContent="space-between"
			alignItems="center"
			width="100%"
		>
			{isSummativeTest && !inReviewMode && !isMobile && (
				<Button
					icon={"help-circle-outline"}
					textColor={theme.colors.white}
					onPress={toggleHelpDialog}
				>
					{t("session.navigation.help")}
				</Button>
			)}
			<TestNavButtons
				showButtonPrevious={showButtonPrevious}
				handlePrevious={handlePrevious}
				disablePreviousButton={disablePreviousButton}
				prevButtonText={prevButtonText}
				showButtonNavSkip={showButtonNavSkip}
				handleNext={handleNext}
				disableNextButton={disableNextButton}
				isSummativeTest={isSummativeTest}
				nextButtonIcon={nextButtonIcon}
				nextButtonMode={nextButtonMode}
				bg={bg}
				nextButtonText={getNextButtonText()}
			/>
		</Box>
	);
};

export default NavButtons;
